import React from 'react';

import rem from 'polished/lib/helpers/rem';
import styled from 'styled-components';

import spacing from '@decisiv/design-tokens/lib/spacing';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import Popover from '@decisiv/ui-components/lib/components/Popover';
import { P } from '@decisiv/ui-components/lib/components/Typography';

type Props = {
  /** The element the panel hangs off — the clipped text, or its control. */
  target: React.RefObject<HTMLElement>;
  /**
   * Called when the panel asks to close itself — Escape, a scroll that carries
   * the anchor away, a click outside. `Popover` is controlled here, so it
   * routes all three through this instead of hiding itself: pass
   * `useTruncationReveal`'s `conceal`, or the reveal is dismissable only by
   * moving the pointer or focus away.
   */
  onRequestHide?: () => void;
  children: React.ReactNode;
};

const Panel = styled(Flex).attrs({ flexDirection: 'column', padding: 1 })`
  max-width: ${rem(spacing.base * 50)};
` as typeof Flex;

/**
 * The panel that carries a clipped cell value in full.
 *
 * This is a `Popover` rather than a `Tooltip` on purpose: a tooltip is a dark
 * charcoal strip meant for a few words of guidance, and reading back a name or
 * an address out of one is hard work. This matches the white panel the
 * complaint chevron already opens, so every overflow in the tables reveals
 * itself the same way.
 *
 * Mount it only while the value is actually being revealed — see
 * `hooks/useTruncationReveal` for why. It is `visible` for as long as it is
 * mounted, and opens itself through no events of its own; every dismissal it
 * decides on comes back through `onRequestHide`.
 */
export default function OverflowPopover({
  target,
  onRequestHide,
  children,
}: Props) {
  return (
    <Popover
      manageEvents={false}
      onRequestHide={onRequestHide}
      target={target}
      visible
      zIndex={5}
    >
      <Panel>
        <P>{children}</P>
      </Panel>
    </Popover>
  );
}
